import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, BookOpen, Clock, Facebook, Instagram, Linkedin, Mail, MapPin, Music, Phone, Twitter, Youtube } from 'lucide-react'
import { NAVIGATION_ITEMS } from '../../utils/constants'
import { useSectionContent } from '../../hooks/useContent'
import { useFooterLogoSettings } from '../../hooks/useFooterLogoSettings'
import { useFooterSettings } from '../../hooks/useFooterSettings'
import { useSocialMediaLinks } from '../../hooks/useSocialMediaLinks'

const socialIconMap: Record<string, React.ComponentType<any>> = {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Music
}

const Footer: React.FC = () => {
  const { getContent, loading } = useSectionContent('contact')
  const { footerLogoSettings } = useFooterLogoSettings()
  const { footerSettings } = useFooterSettings()
  const { activeSocialLinks, socialSettings, loading: socialLoading } = useSocialMediaLinks()

  const contactInfo = {
    address: getContent('address', 'Buddhanagar, New Baneshwor, Kathmandu'),
    phone: getContent('phone', '+977 9867242846'),
    email: getContent('email', 'info@nwlc.com.np'),
    hours: getContent('hours', 'Monday - Friday: 9:00 AM - 6:00 PM')
  }

  const defaultSocials = [
    { id: 'facebook', platform_name: 'Facebook', url: 'https://facebook.com', icon_name: 'Facebook' },
    { id: 'instagram', platform_name: 'Instagram', url: 'https://instagram.com', icon_name: 'Instagram' },
    { id: 'youtube', platform_name: 'YouTube', url: 'https://youtube.com', icon_name: 'Youtube' }
  ]
  const socialLinks = !socialLoading && activeSocialLinks.length ? activeSocialLinks : defaultSocials

  return (
    <footer className="relative overflow-hidden bg-[#0b1422] text-white">
      <div className="container relative py-14 sm:py-16 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_0.7fr_1fr] lg:gap-16">
          <div>
            <Link to="/" className="inline-flex items-center">
              {footerLogoSettings.footer_show_logo_image && footerLogoSettings.footer_logo_url ? (
                <img
                  src={footerLogoSettings.footer_logo_url}
                  alt="Nepal World Language Centre"
                  className="max-h-16 max-w-[260px] object-contain object-left"
                />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-crimson">
                  <BookOpen className="h-6 w-6 text-white" aria-hidden="true" />
                </span>
              )}
            </Link>

            {footerSettings.show_company_info && (
              <p className="mt-6 max-w-lg text-sm leading-7 text-gray-400 sm:text-base">
                {footerSettings.company_description}
              </p>
            )}

            {socialSettings.show_social_links_footer && (
              <div className="mt-7 flex flex-wrap gap-3">
                {socialLinks.map((link) => {
                  const Icon = socialIconMap[link.icon_name] || Facebook
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      aria-label={link.platform_name}
                      title={link.platform_name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-gray-400 transition-all hover:-translate-y-0.5 hover:border-red-500/40 hover:bg-crimson hover:text-white focus:outline-none focus:ring-2 focus:ring-red-400"
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </a>
                  )
                })}
              </div>
            )}
          </div>

          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-white">Explore</h2>
            <nav className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-1" aria-label="Footer navigation">
              {NAVIGATION_ITEMS.map((item) => (
                <Link key={item.name} to={item.href} className="group inline-flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white">
                  {item.name}
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
                </Link>
              ))}
            </nav>
          </div>

          {footerSettings.show_contact_info && (
            <div>
              <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-white">Visit or contact us</h2>
              <div className={`mt-6 space-y-5 text-sm text-gray-400 ${loading ? 'opacity-50' : ''}`}>
                <a href="https://maps.app.goo.gl/JEGWuKF1X14tMd3J7" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 transition-colors hover:text-white">
                  <MapPin className="mt-0.5 h-5 w-5 flex-none text-crimson" aria-hidden="true" />
                  <span className="whitespace-pre-line leading-6">{contactInfo.address}</span>
                </a>
                <a href={`tel:${contactInfo.phone}`} className="flex items-start gap-3 transition-colors hover:text-white">
                  <Phone className="h-5 w-5 flex-none text-crimson" aria-hidden="true" />
                  <span>{contactInfo.phone}</span>
                </a>
                <a href={`mailto:${contactInfo.email}`} className="flex items-start gap-3 transition-colors hover:text-white">
                  <Mail className="h-5 w-5 flex-none text-crimson" aria-hidden="true" />
                  <span>{contactInfo.email}</span>
                </a>
                <div className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-5 w-5 flex-none text-crimson" aria-hidden="true" />
                  <span className="whitespace-pre-line leading-6">{contactInfo.hours}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-7 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
          <p>{footerSettings.copyright_text}</p>
          <a href="https://brandspire.com.np/" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-gray-300">
            Developed by Brandspire Creatives
          </a>
        </div>
      </div>
    </footer>
  )
}

export default Footer
